import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, ArrowRight, Timer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const getTimeLeft = () => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const diff = Math.max(0, end - now);
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor(diff / 3600000) % 24,
    m: Math.floor(diff / 60000) % 60,
    s: Math.floor(diff / 1000) % 60,
  };
};

const OfferPopup = ({ onEnroll }) => {
  const { lang, t } = useLang();
  const [offer, setOffer] = useState(null);
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());

  useEffect(() => {
    if (sessionStorage.getItem("fitnastic_offer_seen")) return;
    api.get("/offer")
      .then((res) => {
        if (res.data && res.data.active) {
          setOffer(res.data);
          setTimeout(() => setVisible(true), 1600);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const close = () => {
    setVisible(false);
    sessionStorage.setItem("fitnastic_offer_seen", "1");
  };

  const title = offer ? (lang === "en" ? offer.title_en : offer.title_bn || offer.title_en) : "";
  const subtitle = offer ? (lang === "en" ? offer.subtitle_en : offer.subtitle_bn || offer.subtitle_en) : "";
  const badge = offer ? (lang === "en" ? offer.badge_en : offer.badge_bn || offer.badge_en) : "";

  const units = [
    { value: timeLeft.d, label: lang === "en" ? "Days" : "দিন" },
    { value: timeLeft.h, label: lang === "en" ? "Hrs" : "ঘণ্টা" },
    { value: timeLeft.m, label: lang === "en" ? "Min" : "মিনিট" },
    { value: timeLeft.s, label: lang === "en" ? "Sec" : "সেকেন্ড" },
  ];

  return (
    <AnimatePresence>
      {visible && offer && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/85 p-5 backdrop-blur-sm"
          onClick={close}
          data-testid="offer-popup-backdrop"
        >
          <motion.div
            initial={{ scale: 0.85, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 30, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 320 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border-2 border-volt bg-panel volt-glow"
            data-testid="offer-popup"
          >
            <div className="absolute -right-10 -top-10 h-36 w-36 rotate-45 bg-volt/10" />
            <button
              data-testid="offer-popup-close-button"
              onClick={close}
              aria-label="Close offer"
              className="absolute right-4 top-4 z-10 rounded-full border border-zinc-700 bg-ink/60 p-1.5 text-zinc-400 transition-colors duration-200 hover:border-volt hover:text-volt"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="p-8">
              <span
                data-testid="offer-popup-badge"
                className="inline-flex items-center gap-1.5 rounded-full bg-volt px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-ink"
              >
                <Zap className="h-3.5 w-3.5" />
                {badge}
              </span>

              <h3 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white" data-testid="offer-popup-title">
                {title}
              </h3>
              <p className="mt-3 text-sm text-zinc-400" data-testid="offer-popup-subtitle">{subtitle}</p>

              <div className="mt-6 flex items-end gap-4" data-testid="offer-popup-pricing">
                {offer.original_price > 0 && (
                  <span className="font-display text-2xl font-bold text-zinc-600 line-through" data-testid="offer-popup-original-price">
                    ₹{offer.original_price.toLocaleString("en-IN")}
                  </span>
                )}
                <span className="font-display text-6xl font-extrabold leading-none text-volt" data-testid="offer-popup-offer-price">
                  ₹{offer.offer_price.toLocaleString("en-IN")}
                </span>
              </div>

              <div className="mt-6" data-testid="offer-countdown">
                <p className="mb-2.5 flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-zinc-500" data-testid="offer-countdown-label">
                  <Timer className="h-3.5 w-3.5 text-volt" />
                  {t.offer_ends_in}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {units.map((u) => (
                    <div key={u.label} className="rounded-lg border border-zinc-800 bg-ink px-2 py-2.5 text-center" data-testid={`countdown-${u.label}`}>
                      <p className="font-display text-2xl font-extrabold leading-none text-volt tabular-nums">
                        {String(u.value).padStart(2, "0")}
                      </p>
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-zinc-500">{u.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <button
                data-testid="offer-popup-enroll-button"
                onClick={() => { close(); onEnroll(offer.package_slug); }}
                className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-volt py-4 font-display text-base font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.02]"
              >
                {t.enroll_now}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OfferPopup;
